import { Box, IconButton, Switch } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Reorder as ReorderIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MenuService } from '../../../api/menuService';
import { MenuItemResponse } from '../../../@type/menu.types';
import ActionButton from '../../../components/common/buttons/ActionButton';
import CancelButton from '../../../components/common/buttons/CancelButton';
import Card from '../../../components/common/Card';
import MuiTable from '../../../components/common/MuiTable';
import { Column } from '../../../utils/constants';
import { toast } from 'react-toastify';

const MenuList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menus, setMenus] = useState<MenuItemResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [togglingIds, setTogglingIds] = useState<number[]>([]);

  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    setLoading(true);
    try {
      const response = await MenuService.getAllMenuItems();
      console.log('API Response:', response.data);
      const content = response.data?.data || [];
      setMenus(content);
    } catch (error) {
      console.error('Failed to load menus:', error);
      setMenus([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('Are you sure you want to delete this menu item?'))) {
      try {
        await MenuService.deleteMenuItem(id);
        toast.success(t('menus.deleteSuccess'));
        loadMenus();
      } catch (error) {
        console.error('Failed to delete menu:', error);
        toast.error('Failed to delete menu item');
      }
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    setTogglingIds((prev) => [...prev, id]);
    try {
      await MenuService.updateMenuItem(id, { is_active: isActive });
      setMenus((prev) => prev.map((menu) => (menu.id === id ? { ...menu, is_active: isActive } : menu)));
      toast.success(t('menus.updateSuccess'));
    } catch (error) {
      console.error('Failed to update menu status:', error);
      toast.error(t('menus.updateError'));
    } finally {
      setTogglingIds((prev) => prev.filter((menuId) => menuId !== id));
    }
  };

  const handleEdit = (id: number) => {
    navigate(`/settings/menus/${id}/edit`);
  };

  const header: Column[] = [
    {
      id: 'id',
      label: t('common.table.id'),
      minWidth: 50,
      align: 'center',
    },
    {
      id: 'code',
      label: t('menus.menuCode'),
      minWidth: 120,
      align: 'left',
    },
    {
      id: 'name_en',
      label: t('common.table.name'),
      minWidth: 150,
      align: 'left',
      format: (value: string) => {
        return value || '-';
      },
    },
    {
      id: 'icon_path',
      label: t('menus.iconPath'),
      minWidth: 200,
      align: 'left',
      format: (value: string) => {
        return (
          <Box component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>
            {value}
          </Box>
        );
      },
    },
    {
      id: 'route_name',
      label: t('menus.routeName'),
      minWidth: 150,
      align: 'left',
      format: (value: string) => {
        return (
          <Box component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>
            {value}
          </Box>
        );
      },
    },
    {
      id: 'sort_order',
      label: t('menus.sortOrder'),
      minWidth: 80,
      align: 'center',
      format: (value: number) => {
        return (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: 14,
              fontWeight: 500,
              color: 'text.primary',
              backgroundColor: 'action.selected',
            }}
          >
            {value}
          </Box>
        );
      },
    },
    {
      id: 'is_active',
      label: t('common.table.status'),
      minWidth: 80,
      align: 'center',
      format: (value: boolean, row?: { id: number }) => {
        return (
          <Switch
            checked={value}
            disabled={!row || togglingIds.includes(row.id)}
            onChange={(_, checked) => row && handleToggleActive(row.id, checked)}
            size="small"
          />
        );
      },
    },
    {
      id: 'action',
      label: t('common.table.actions'),
      minWidth: 100,
      align: 'center',
      format: (id: number) => {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <IconButton
              size="small"
              onClick={() => handleEdit(id)}
            >
              <EditIcon color="success" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDelete(id)}
            >
              <DeleteIcon color="error" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // Transform data to include action and name_en fields
  const dataTable = menus.map((menu) => ({
    ...menu,
    name_en: menu.translations?.en?.name || '-',
    action: menu.id,
  }));

  return (
    <Card>
      <Box className="mb-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t('menus.title')}</h2>
        <Box className="flex gap-2">
          <CancelButton
            startIcon={<ReorderIcon />}
            onClick={() => navigate('/settings/menus/reorder')}
          >
            {t('menus.dragToReorder')}
          </CancelButton>
          <ActionButton
            startIcon={<AddIcon />}
            onClick={() => navigate('/settings/menus/create')}
          >
            {t('menus.create')}
          </ActionButton>
        </Box>
      </Box>

      <MuiTable header={header} data={dataTable} isLoading={loading} />
    </Card>
  );
};

export default MenuList;
